import { DependencyList, useCallback, useState, useRef } from 'react';
import { useMountedState } from 'react-use';
import { IErrorData } from '@zmedia/bm-soccer-ui/interface';
import { asyncAction } from '@zmedia/bm-soccer-ui/utils/async';
import { Logger } from '../utils';

export type AsyncState<T> =
    | {
          loading: boolean;
          error?: undefined | IErrorData;
          value?: undefined;
      }
    | {
          loading: false;
          error: IErrorData;
          value?: undefined;
      }
    | {
          loading: false;
          error?: undefined | IErrorData;
          value: T;
      };

export type AsyncFn<Result = any, Args extends any[] = any[]> = [
    AsyncState<Result>,
    (...args: Args | any) => any
];

export function useAsyncFn<Result = any, Args extends any[] = any[]>(
    fn: (...args: Args) => any,
    deps: DependencyList = [],
    initialState: AsyncState<Result> = { loading: false }
): AsyncFn<Result, Args> {
    const lastCallId = useRef(0);
    const [state, set] = useState<AsyncState<Result>>(initialState);
    const isMounted = useMountedState();

    const callback = useCallback((...args: Args) => {
        const callId = ++lastCallId.current;
        set({ loading: true });
        return asyncAction(() => {
            return fn(...args);
        }).then(
            (value) => {
                if (isMounted() && callId === lastCallId.current) {
                    set({ value, loading: false });
                }
                return value;
            },
            (error) => {
                if (isMounted() && callId === lastCallId.current) {
                    set({ error, loading: false });
                }

                if (error) {
                    Logger.error(
                        error.err || 'fetchFail',
                        'useAsyncFn Fail|',
                        error
                    );
                }

                return error;
            }
        );
    }, deps);

    return [state, callback];
}
